/**
 * @fileoverview NotificationDetailModal Component - Detailed notification view
 * 
 * Provides a comprehensive detailed view of a single notification with:
 * - Full notification content display
 * - Priority-based styling
 * - Action button navigation
 * - Mark as read functionality
 * - Sharp, no-curve design
 * 
 * @component
 * @example
 * <NotificationDetailModal 
 *   isOpen={true} 
 *   notification={notificationData} 
 *   onClose={() => {}} 
 *   onMarkAsRead={() => {}}
 * />
 * 
 * @requires React
 * @requires react-router-dom
 * @requires ./NotificationDetailModal.css
 * 
 * @author By John Bellew - Infinia.ie - last updated 22/07/2025
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiHelper from '../utils/apiHelper';
import './NotificationDetailModal.css';

/**
 * NotificationDetailModal Component
 * 
 * Renders a detailed view of a single notification.
 * 
 * Features:
 * - Full notification content display
 * - Priority-based styling (High = red, Medium = orange, Low = gray)
 * - Rich HTML message rendering
 * - Action button navigation
 * - Mark as read functionality
 * - Sharp, geometric design
 * 
 * @component
 * @param {Object} props Component props
 * @param {boolean} props.isOpen Whether the modal is visible
 * @param {Object} props.notification Notification object to display
 * @param {Function} props.onClose Callback function to close the modal
 * @param {Function} props.onMarkAsRead Callback to mark notification as read
 * @param {Function} props.onMarkAsUnread Callback to mark notification as unread
 * @param {string} props.userEmail User's email address for API calls
 * @returns {JSX.Element} NotificationDetailModal component
 */
function NotificationDetailModal({ isOpen, notification, onClose, onMarkAsRead, onMarkAsUnread, userEmail }) {
  const navigate = useNavigate();
  const [localNotification, setLocalNotification] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Update local notification when prop changes
  useEffect(() => {
    if (notification) {
      setLocalNotification(notification);
    }
  }, [notification]);

  /**
   * Get priority-based CSS class
   * @param {string} priority - Notification priority (High/Medium/Low)
   * @returns {string} CSS class name
   */
  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'High': return 'priority-high';
      case 'Medium': return 'priority-medium';
      case 'Low': return 'priority-low';
      default: return 'priority-medium';
    }
  };

  /**
   * Get notification type icon
   * @param {string} type - Notification type
   * @returns {string} FontAwesome icon class
   */
  const getTypeIcon = (type) => {
    switch (type) {
      case 'News': return 'fas fa-newspaper';
      case 'Event': return 'fas fa-calendar-alt';
      case 'Survey': return 'fas fa-poll';
      case 'Alert': return 'fas fa-exclamation-triangle';
      case 'Update': return 'fas fa-sync-alt';
      case 'Info': return 'fas fa-info-circle';
      default: return 'fas fa-bell';
    }
  };

  /**
   * Format date for display
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted date
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /**
   * Handle action button click
   */
  const handleActionClick = () => {
    // Mark as read if not already read
    if (!localNotification.isRead && onMarkAsRead) {
      onMarkAsRead(localNotification.id);
    }

    // Navigate to action URL if provided
    if (localNotification.actionUrl) {
      navigate(localNotification.actionUrl);
      onClose(); // Close modal after navigation
    }
  };

  /**
   * Handle toggle read/unread status
   */
  const handleToggleReadStatus = async () => {
    if (isUpdating || !userEmail) return;

    setIsUpdating(true);
    try {
      if (localNotification.isRead) {
        // Mark as unread
        await apiHelper.markNotificationUnread(localNotification.id, userEmail);
        setLocalNotification(prev => ({ ...prev, isRead: false }));
        
        // Notify parent component
        if (onMarkAsUnread) {
          onMarkAsUnread(localNotification.id);
        }
      } else {
        // Mark as read
        await apiHelper.markNotificationRead(localNotification.id, userEmail);
        setLocalNotification(prev => ({ ...prev, isRead: true }));
        
        // Notify parent component
        if (onMarkAsRead) {
          onMarkAsRead(localNotification.id);
        }
      }
    } catch (error) {
      console.error('Error toggling notification status:', error);
      // Could add toast notification here
    } finally {
      setIsUpdating(false);
    }
  };

  if (!isOpen || !localNotification) return null;

  return (
    <div className="notification-detail-overlay" onClick={onClose}>
      <div className="notification-detail-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`notification-detail-header ${getPriorityClass(localNotification.priority)}`}>
          <div className="notification-detail-header-left">
            <div className="notification-detail-icon">
              <i className={getTypeIcon(localNotification.type)}></i>
            </div>
            <div className="notification-detail-meta">
              <span className="notification-detail-type">{localNotification.type}</span>
              <span className="notification-detail-priority">{localNotification.priority} Priority</span>
            </div>
          </div>
          <div className="notification-detail-header-right">
            <button 
              className={`mark-read-btn-detail ${localNotification.isRead ? 'mark-unread' : 'mark-read'}`}
              onClick={handleToggleReadStatus}
              disabled={isUpdating}
              title={localNotification.isRead ? 'Mark as unread' : 'Mark as read'}
            >
              {isUpdating ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Updating...
                </>
              ) : localNotification.isRead ? (
                <>
                  <i className="fas fa-eye-slash"></i>
                  Mark as Unread
                </>
              ) : (
                <>
                  <i className="fas fa-check"></i>
                  Mark as Read
                </>
              )}
            </button>
            <button 
              className="notification-detail-close-btn"
              onClick={onClose}
              title="Close"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="notification-detail-body">
          {/* Title */}
          <h2 className="notification-detail-title">{localNotification.title}</h2>
          
          {/* Timestamp */}
          <div className="notification-detail-timestamp">
            <i className="fas fa-clock"></i>
            {formatDate(localNotification.created)}
          </div>

          {/* Message */}
          <div 
            className="notification-detail-message"
            dangerouslySetInnerHTML={{ __html: localNotification.message }}
          />

          {/* Status Badge */}
          <div className="notification-detail-status">
            {localNotification.isRead ? (
              <span className="status-badge read">
                <i className="fas fa-check-circle"></i>
                Read
              </span>
            ) : (
              <span className="status-badge unread">
                <i className="fas fa-circle"></i>
                Unread
              </span>
            )}
          </div>
        </div>

        {/* Footer */}
        {localNotification.actionUrl && (
          <div className="notification-detail-footer">
            <button 
              className="notification-detail-action-btn"
              onClick={handleActionClick}
            >
              <i className="fas fa-arrow-right"></i>
              View Details
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationDetailModal; 